/* eslint-disable react/no-children-map */ // HACK: Don't use Children.map here
/* eslint-disable react/no-clone-element */ // HACK: Don't use cloneElement here
/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { ReactNode, RefObject } from 'react';

import { Children, cloneElement, createRef, isValidElement, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export type ResizableContainerProps = {
    className?: string;
    targetElement?: RefObject<HTMLDivElement | null>;
    resizeFunction?: () => void;
    children: ReactNode;
};

export function ResizableContainer({
    className,
    targetElement,
    resizeFunction,
    children,
}: ResizableContainerProps) {
    const elementRef = useRef<HTMLDivElement>(null);
    const [isHidden, setIsHidden] = useState<boolean>(true);
    const [elementHeight, setElementHeight] = useState<number | null>(null);
    const [elementWidth, setElementWidth] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            if (elementRef.current) {
                // Temporarily hide the element
                setIsHidden(true);

                // Allow the component to re-render with the hidden class applied
                const timeout = setTimeout(() => {
                    setElementHeight(elementRef.current!.clientHeight);
                    setElementWidth(elementRef.current!.clientWidth);

                    if (resizeFunction !== undefined) {
                        resizeFunction();
                    }

                    // Show the element again after dimensions are set
                    setIsHidden(false);
                }, 0); // Set timeout to allow state change to take effect

                return () => clearTimeout(timeout);
            }
        };

        // Attach the event listener
        window.addEventListener('resize', handleResize);

        // Initial calculation
        handleResize();

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- HACK:
    }, [elementRef]);

    const applyStylesRecursively = (child: ReactNode): ReactNode => {
        if (!isValidElement(child)) {
            return child;
        }

        // eslint-disable-next-line react/no-create-ref -- TODO: Refactor to use `useRef` instead of `createRef`
        const childRef = createRef<HTMLElement>();
        const clonedChild = cloneElement(child as React.ReactElement<any>, {
            ref: childRef,
        });

        if (childRef.current === targetElement?.current) {
            return cloneElement(clonedChild, {
                style: {
                    height: elementHeight ?? 'auto',
                    maxHeight: elementHeight ?? 'auto',
                    width: elementWidth ?? 'auto',
                    maxWidth: elementWidth ?? 'auto',
                    ...clonedChild.props.style,
                },
            });
        }

        if (clonedChild.props.children) {
            return cloneElement(clonedChild, {
                children: Children.map(
                    clonedChild.props.children,
                    applyStylesRecursively,
                ),
            });
        }

        return clonedChild;
    };

    return (
        <div ref={elementRef} className="w-full h-full">
            <div
                className={cn(className, {
                    hidden: isHidden,
                })}
                style={
                    targetElement === undefined
                        ? {
                                height: elementHeight ?? 'auto',
                                maxHeight: elementHeight ?? 'auto',
                                width: elementWidth ?? 'auto',
                                maxWidth: elementWidth ?? 'auto',
                            }
                        : {}
                }
            >
                {Children.map(children, applyStylesRecursively)}
            </div>
        </div>
    );
}
